import React, { useEffect, useRef } from "react";
import { DynamicTextContext, useDynamicTextProxy } from "@concord-consortium/dynamic-text";

import { useAutoHeight } from "../hooks/use-auto-height";
import { useHint } from "../hooks/use-hint";
import { useRequiredQuestion } from "../hooks/use-required-question";
import { useShutterbug } from "../hooks/use-shutterbug";
import { BaseAuthoring, IBaseAuthoringProps } from "./base-authoring";
import { SubmitButton } from "./submit-button";
import { LockedInfo } from "./locked-info";
import {
  IAuthoringMetadata, IRuntimeMetadata, setSupportedFeatures, useAuthoredState, useInitMessage, useInteractiveState,
  IReportInitInteractive,
  useAccessibility
} from "@concord-consortium/lara-interactive-api";
import { ObjectStorageConfig, ObjectStorageProvider } from "@concord-consortium/object-storage";
import { IBaseAuthoredState, UpdateFunc, IAuthoringComponentProps, IRuntimeComponentProps } from "./base-app";
import { useBasicLogging } from "../hooks/use-basic-logging";
import { useLinkedInteractives } from "../hooks/use-linked-interactives";
import { ILinkedInteractiveProp } from "../hooks/use-linked-interactives-authoring";
import { InitMessageContext } from "../hooks/use-context-init-message";

import css from "./base-app.scss";

interface IBaseQuestionAuthoredState extends IBaseAuthoredState {
  hint?: string;
  required?: boolean;
  predictionFeedback?: string;
}

interface IBaseQuestionInteractiveState {
  submitted?: boolean;
}

export interface IRuntimeQuestionComponentProps<IAuthoredState, IInteractiveState> extends IRuntimeComponentProps<IAuthoredState> {
  interactiveState?: IInteractiveState | null;
  setInteractiveState?: (updateFunc: UpdateFunc<IInteractiveState>) => void;
  report?: boolean;
  view?: "standalone";
}

interface IProps<IAuthoredState, IInteractiveState> {
  // Question can provide either fully custom Authoring component or use BaseAuthoring (that uses react-jsonschema-form).
  Authoring?: React.FC<IAuthoringComponentProps<IAuthoredState>>;
  baseAuthoringProps?: Omit<IBaseAuthoringProps<IAuthoredState>, "authoredState" | "setAuthoredState">;
  Runtime: React.FC<IRuntimeQuestionComponentProps<IAuthoredState, IInteractiveState>>;
  disableAutoHeight?: boolean;
  disableSubmitBtnRendering?: boolean;
  // Note that isAnswered is required when `disableSubmitBtnRendering` is false.
  isAnswered?: (state: IInteractiveState | null, authoredState?: IAuthoredState) => boolean;
  linkedInteractiveProps?: ILinkedInteractiveProp[];
  migrateAuthoredState?: (oldAuthoredState: any) => IAuthoredState;
  allowEmptyAuthoredStateAtRuntime?: boolean;
}

// BaseApp for interactives that save interactive state and show in the report. E.g. open response, multiple choice.
export const BaseQuestionApp = <IAuthoredState extends IAuthoringMetadata & IBaseQuestionAuthoredState,
  IInteractiveState extends IRuntimeMetadata & IBaseQuestionInteractiveState>(props: IProps<IAuthoredState, IInteractiveState>) => {
  const { Authoring, baseAuthoringProps, Runtime, isAnswered, disableAutoHeight, disableSubmitBtnRendering,
    linkedInteractiveProps, migrateAuthoredState, allowEmptyAuthoredStateAtRuntime } = props;
  const container = useRef<HTMLDivElement>(null);
  const useAuthStateResult = useAuthoredState<IAuthoredState>();
  const authoredState = migrateAuthoredState && useAuthStateResult.authoredState ?
    migrateAuthoredState(useAuthStateResult.authoredState) : useAuthStateResult.authoredState;
  const setAuthoredState = useAuthStateResult.setAuthoredState;
  const { interactiveState, setInteractiveState } = useInteractiveState<IInteractiveState>();
  const initMessage = useInitMessage();
  const isLoading = !initMessage;
  const isRuntimeView = initMessage?.mode === "runtime";
  const objectStorageConfig = (initMessage?.mode === "runtime" || initMessage?.mode === "report" ? initMessage.objectStorageConfig : undefined) as any as ObjectStorageConfig | undefined;

  const dynamicTextProxy = useDynamicTextProxy();

  useAutoHeight({ container: container.current, disabled: isRuntimeView && disableAutoHeight || isLoading });
  useHint();
  useRequiredQuestion(initMessage);
  useShutterbug({ container: "." + css.runtime });
  useBasicLogging({ disabled: !isRuntimeView });
  useLinkedInteractives(linkedInteractiveProps?.map(li => li.label), initMessage);
  useAccessibility({
    updateHtmlFontSize: true,
    addBodyClass: true,
    fontFamilySelector: "[data-font-family-override]",
  });

  useEffect(() => {
    setSupportedFeatures({
      interactiveState: true,
      authoredState: true
    });
  }, [initMessage]);

  // Some interactives, like full-screen, may not require authored state to render at runtime, and can signal that
  // by setting allowEmptyAuthoredStateAtRuntime to true. In the case of the full-screen interactive it originally
  // did not require authored state to render because it just used a query parameter for the URL that it was "wrapping"
  // but it has since been updated to optionally use authored state.
  const resolvedAuthoredState = !authoredState && allowEmptyAuthoredStateAtRuntime ? {} as IAuthoredState : authoredState;

  if (!isAnswered && !disableSubmitBtnRendering) {
    throw new Error("isAnswered function is required when disableSubmitBtnRendering = false");
  }

  const renderAuthoring = () => {
    if (Authoring) {
      return <Authoring authoredState={authoredState} setAuthoredState={setAuthoredState} />;
    }
    if (!Authoring && baseAuthoringProps) {
      return <BaseAuthoring
        {...baseAuthoringProps}
        linkedInteractiveProps={linkedInteractiveProps}
        authoredState={authoredState}
        setAuthoredState={setAuthoredState}
      />;
    }
  };

  const renderRuntime = () => {
    if (!resolvedAuthoredState) {
      return "Authored state is missing.";
    }

    return (
      <ObjectStorageProvider config={objectStorageConfig}>
        <div className={css.runtime} data-font-family-override="true">
          <Runtime authoredState={resolvedAuthoredState} interactiveState={interactiveState} setInteractiveState={setInteractiveState} />
          {
            !disableSubmitBtnRendering &&
            <div>
              <SubmitButton isAnswered={!!isAnswered?.(interactiveState, resolvedAuthoredState)} />
              <LockedInfo />
            </div>
          }
        </div>
      </ObjectStorageProvider>
    );
  };

  const renderReport = () => {
    const reportInitMessage = initMessage as IReportInitInteractive;
    const view = reportInitMessage?.view;
    if (!resolvedAuthoredState) {
      return "Authored state is missing.";
    }
    return (
      <ObjectStorageProvider config={objectStorageConfig}>
        <div className={css.runtime}>
          <Runtime authoredState={resolvedAuthoredState} interactiveState={interactiveState} setInteractiveState={setInteractiveState} report={true} view={view} />
          { resolvedAuthoredState?.required && <div>Question has been { interactiveState?.submitted ? "" : "NOT" } submitted.</div> }
        </div>
      </ObjectStorageProvider>
    );
  };

  const renderMode = () => {
    switch (initMessage?.mode) {
      case "authoring":
        return renderAuthoring();
      case "runtime":
        return renderRuntime();
      case "report":
        return renderReport();
      default:
        return "Loading...";
    }
  };

  return (
    <div ref={container}>
      <InitMessageContext.Provider value={initMessage}>
        <DynamicTextContext.Provider value={dynamicTextProxy}>
          { renderMode() }
        </DynamicTextContext.Provider>
      </InitMessageContext.Provider>
    </div>
  );
};
